/**
 * Lightweight client-side router using the History API.
 *
 * Path shape:
 *   /                  → dashboard
 *   /<page>            → top-level page (anfragen, pflege, betreuung, …)
 *   /<page>/<sub>      → sub-resource (e.g. /pflege/123, /pflege/new)
 *
 * No react-router dependency — for a 7-page admin SPA the overhead isn't
 * worth it. The hook subscribes to popstate so Back/Forward work; navigate()
 * uses pushState so deep links can be copied from the URL bar.
 */
import { useCallback, useEffect, useState } from 'react';

export type PageId =
  | 'dashboard'
  | 'anfragen'
  | 'anfragen_bk'
  | 'anfragen_beendet'
  | 'anfragen_bk_beendet'
  | 'chat'
  | 'email'
  | 'kalender'
  | 'matching'
  | 'pflege'
  | 'betreuung'
  | 'benutzer'
  | 'rollen'
  | 'profil'
  | 'einstellungen'
  | 'admin_einstellungen';

/**
 * Laufzeit-Spiegel von `PageId`. MUSS vollständig sein: `parsePath()` erkennt
 * nur hier gelistete Segmente als Seite (alles andere landet auf dashboard),
 * und die Sidebar baut daraus die `href`s ihrer Menüpunkte. `chat` fehlte —
 * der Menüpunkt war dadurch weder verlinkbar noch per Adresse erreichbar.
 */
export const PAGE_IDS: PageId[] = [
  'dashboard', 'anfragen', 'anfragen_bk', 'anfragen_beendet', 'anfragen_bk_beendet',
  'chat', 'email', 'kalender', 'matching',
  'pflege', 'betreuung', 'benutzer', 'rollen', 'profil', 'einstellungen',
  'admin_einstellungen',
];

export interface RouteState {
  page: PageId;
  /** First path segment after page id — typically a resource id or "new". */
  sub: string | null;
  /** Second path segment — tab slug (PB detail) or account member id. */
  tab: string | null;
  /**
   * Third path segment — used by the Familienkonto detail route
   * (/pflege/{accountId}/{profileId}/{pbTab}) to carry the active PB tab.
   */
  extra: string | null;
}

function isPageId(s: string): s is PageId {
  return (PAGE_IDS as string[]).includes(s);
}

export function parsePath(pathname: string): RouteState {
  const parts = pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (parts.length === 0) return { page: 'dashboard', sub: null, tab: null, extra: null };
  const [first, second, third, fourth] = parts;
  if (isPageId(first)) return { page: first, sub: second ?? null, tab: third ?? null, extra: fourth ?? null };
  return { page: 'dashboard', sub: null, tab: null, extra: null };
}

export function buildPath(page: PageId, sub?: string | null, tab?: string | null, extra?: string | null): string {
  if (page === 'dashboard' && !sub) return '/';
  if (!sub) return `/${page}`;
  if (!tab) return `/${page}/${sub}`;
  if (!extra) return `/${page}/${sub}/${tab}`;
  return `/${page}/${sub}/${tab}/${extra}`;
}

/* ─── Familienkonto-Routen (/pflege) ──────────────────────────────────────
 *
 * Die Pflege-Seite bündelt Familienkonten. Führend ist die CRM-1.0-ID
 * (`users.id`), auch in der URL — Entscheidung Damon, 23.09.2026. URL-Schema:
 *
 *   /pflege                               → Kontenliste
 *   /pflege/new                           → neues Konto anlegen
 *   /pflege/konto/{accountId}             → Konto-Detail (Pflegeempfänger
 *                                            bzw. erste Person aktiv) —
 *                                            EIGENE Form, weil ein nacktes
 *                                            /pflege/{n} sonst nicht zwischen
 *                                            „Konto n" und „User n" unter-
 *                                            scheidbar wäre.
 *   /pflege/{userId}                      → Person (users.id), Default-Tab
 *   /pflege/{userId}/{tab}                → Person + PB-Tab
 *
 *   Kompatibilitätsform (alte Deep-Links, VOR 23.09.2026 verschickt):
 *   /pflege/{accountId}/{profileId}       → wird per profiles.id → users.id
 *   /pflege/{accountId}/{profileId}/{tab}   aufgelöst und auf die neue Form
 *                                            umgeleitet (history.replaceState).
 *
 * Segmente: sub = accountId|userId|"konto"|"new", tab = tabSlug|profileId
 * (Kompat) je nach Form, extra = PB-Tab (nur Kompatform, drittes Segment).
 *
 * Erkennung Kompatform: sub UND tab sind beide rein numerisch — die neue
 * Form hat als zweites Segment immer einen Tab-Slug (String) oder gar
 * nichts. `/pflege/42/7` (zwei Zahlen) ist also IMMER die alte Form.
 */
export interface FamilyRoute {
  /** Listen-Ansicht (kein sub). */
  isList: boolean;
  /** „Neues Konto"-Ansicht (/pflege/new). */
  isNew: boolean;
  /** Konto-only-Ansicht (/pflege/konto/{accountId}), kein bestimmter User. */
  isAccountOnly: boolean;
  /** Alte Form (/pflege/{accountId}/{profileId}[/{tab}]) — muss aufgelöst und umgeleitet werden. */
  isLegacy: boolean;
  /** Konto-ID — gesetzt bei isAccountOnly UND isLegacy, sonst null. */
  accountId: number | null;
  /** Profil-ID (numerisches zweites Segment) — nur bei isLegacy gesetzt, sonst null. */
  profileId: number | null;
  /** users.id der angezeigten Person — gesetzt in der kanonischen Form, sonst null. */
  userId: number | null;
  /** PB-Tab-Slug oder null → Default-Tab. Zweites Segment (kanonisch) bzw. drittes (Kompat). */
  tabSlug: string | null;
}

export function parseFamilyRoute(
  sub: string | null,
  tab: string | null,
  extra: string | null = null,
): FamilyRoute {
  const empty: FamilyRoute = {
    isList: false, isNew: false, isAccountOnly: false, isLegacy: false,
    accountId: null, profileId: null, userId: null, tabSlug: null,
  };

  if (!sub) return { ...empty, isList: true };
  if (sub === 'new') return { ...empty, isNew: true };

  if (sub === 'konto') {
    const accountId = tab != null && /^\d+$/.test(tab) ? Number(tab) : null;
    return { ...empty, isAccountOnly: accountId != null, accountId, tabSlug: extra ?? null };
  }

  if (!/^\d+$/.test(sub)) return { ...empty, isList: true };
  const subNum = Number(sub);
  const tabIsNumeric = tab != null && /^\d+$/.test(tab);

  if (tabIsNumeric) {
    // Kompat: /pflege/{accountId}/{profileId}[/{tab}]
    return {
      ...empty, isLegacy: true,
      accountId: subNum, profileId: Number(tab), tabSlug: extra ?? null,
    };
  }

  // Kanonisch: /pflege/{userId}[/{tab}]
  return { ...empty, userId: subNum, tabSlug: tab ?? null };
}

/**
 * Hook: returns the current route + a `navigate` function that updates the URL.
 *
 * - `navigate(page, sub, { replace })` updates history then triggers a state
 *   change (same as popstate would).
 * - The component re-renders on browser Back/Forward via the `popstate` listener.
 */
/**
 * WP8 — Globale Navigation außerhalb des useRoute-Hooks.
 *
 * Für Komponenten ohne `navigate`-Prop (z.B. der geteilte CaregiverDetail-
 * Drawer, der von BetreuungPage UND BkAnfragenPage gerendert wird): schreibt
 * den neuen Pfad via pushState und feuert ein synthetisches `popstate`, auf
 * das der useRoute-Hook in App.tsx bereits hört → App re-rendert die Route,
 * ohne dass ein Callback durch alle Ebenen gereicht werden muss (rein
 * additiver Einbau, keine bestehenden Props/Zeilen ändern).
 */
export function navigateGlobal(
  page: PageId,
  sub?: string | null,
  tab?: string | null,
  extra?: string | null,
): void {
  const path = buildPath(page, sub, tab, extra);
  if (window.location.pathname !== path) {
    window.history.pushState(null, '', path);
  }
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useRoute(): RouteState & {
  navigate: (
    page: PageId,
    sub?: string | null,
    tab?: string | null,
    extra?: string | null,
    opts?: { replace?: boolean },
  ) => void;
} {
  const [route, setRoute] = useState<RouteState>(() =>
    parsePath(typeof window !== 'undefined' ? window.location.pathname : '/'),
  );

  useEffect(() => {
    const handler = () => setRoute(parsePath(window.location.pathname));
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  const navigate = useCallback((
    page: PageId,
    sub?: string | null,
    tab?: string | null,
    extra?: string | null,
    opts?: { replace?: boolean },
  ) => {
    const path = buildPath(page, sub, tab, extra);
    if (window.location.pathname === path) return;
    if (opts?.replace) window.history.replaceState(null, '', path);
    else window.history.pushState(null, '', path);
    setRoute({ page, sub: sub ?? null, tab: tab ?? null, extra: extra ?? null });
  }, []);

  return { ...route, navigate };
}
